import React, { FC, useMemo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Box, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { PedigreePersonNodeData } from '../types';
import classes from './PersonNode.module.css';
import { getContrastingColor } from '../utils';
import { useGraphData } from '../hooks/useGraphData';
import cx from 'clsx';

const NODE_SIZE = 80; // Size of the node (80x80px)
const BORDER_WIDTH = 2; // Default border width for standard shapes

// -----------------------------------------------------------------------------
interface PersonNodeProps extends NodeProps {
  onContextMenu?: (event: React.MouseEvent, nodeId: string) => void;
}

// -----------------------------------------------------------------------------
export const PersonNode: FC<PersonNodeProps> = ({
  isConnectable = true,
  id,
  onContextMenu,
}) => {
  // ---------------------------------------------------------------------------
  const { t } = useTranslation('pedigree');
  const { getNodeData } = useGraphData();
  const personData = useMemo(() => {
    return getNodeData(Number(id)) as PedigreePersonNodeData;
  }, [getNodeData, id]);

  // ---------------------------------------------------------------------------
  const calculatedAge = useMemo(() => {
    if (personData?.isDeceased && personData.ageAtDeath) {
      return String(personData.ageAtDeath);
    }

    const birthYear = personData?.birthYear;
    if (!birthYear || birthYear.trim() === '') {
      return null;
    }

    const birth = parseInt(birthYear, 10);
    if (isNaN(birth)) {
      return null;
    }

    const currentYear = new Date().getFullYear();
    const age = currentYear - birth;

    if (age < 0 || age > 150) {
      return null;
    }

    return age.toString();
  }, [personData?.ageAtDeath, personData?.birthYear, personData?.isDeceased]);

  // ---------------------------------------------------------------------------
  const displayingHeredity =
    personData?.heredity && personData.heredity !== 'none';

  // ---------------------------------------------------------------------------
  if (!personData) {
    return null;
  }

  // ---------------------------------------------------------------------------
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onContextMenu && id) {
      onContextMenu(event, id);
    }
  };

  // ---------------------------------------------------------------------------
  const getPersonShape = () => {
    // Standard pedigree shapes
    if (personData.gender === 'M') {
      return { borderRadius: '0px' }; // Square for males
    } else if (personData.gender === 'F') {
      return { borderRadius: '50%' }; // Circle for females
    } else if (personData.gender === 'U') {
      return {
        borderRadius: '0px',
        width: NODE_SIZE,
        height: NODE_SIZE,
        border: 'none', // Remove border since we'll use SVG
        padding: '0',
      };
    } else {
      return {
        borderRadius: '0px',
        width: NODE_SIZE,
        height: NODE_SIZE,
        border: 'none', // Remove border since we'll use SVG
        padding: '0',
      };
    }
  };

  // ---------------------------------------------------------------------------
  const getPersonStyle = () => {
    const baseStyle = {
      width: NODE_SIZE,
      height: NODE_SIZE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative' as const,
      backgroundColor: '#ffffff',
      border: `${BORDER_WIDTH}px solid #333`,
      cursor: 'pointer',
      boxSizing: 'border-box' as const,
      ...getPersonShape(),
    };

    // Remove gender-specific colors and use neutral styling
    if (personData.gender === 'U' || personData.gender === 'O') {
      // For special shapes, make background transparent since SVG handles the fill
      baseStyle.backgroundColor = 'transparent';
      baseStyle.border = 'none';
    }

    // Deceased styling (diagonal line)
    if (personData.isDeceased) {
      baseStyle.position = 'relative';
    }

    return baseStyle;
  };

  // ---------------------------------------------------------------------------
  const renderPatternDefs = (conditions: any[]) => {
    return (
      <defs>
        {conditions.map((condition: any) => {
          const pattern = condition.pattern || 'solid';
          if (pattern === 'solid') {
            return null;
          }

          const contrastColor = getContrastingColor(condition.color);

          return (
            <pattern
              key={`pattern-${condition.id}`}
              id={`pattern-${condition.id}-${id}`}
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
            >
              <rect width="6" height="6" fill={condition.color} />
              {pattern === 'horizontal' && (
                <>
                  <line
                    x1="0"
                    y1="2"
                    x2="6"
                    y2="2"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                  <line
                    x1="0"
                    y1="4"
                    x2="6"
                    y2="4"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                </>
              )}
              {pattern === 'vertical' && (
                <>
                  <line
                    x1="2"
                    y1="0"
                    x2="2"
                    y2="6"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                  <line
                    x1="4"
                    y1="0"
                    x2="4"
                    y2="6"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                </>
              )}
              {pattern === 'diagonal' && (
                <>
                  <line
                    x1="0"
                    y1="0"
                    x2="6"
                    y2="6"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                  <line
                    x1="0"
                    y1="3"
                    x2="3"
                    y2="6"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                  <line
                    x1="3"
                    y1="0"
                    x2="6"
                    y2="3"
                    stroke={contrastColor}
                    strokeWidth="1.2"
                  />
                </>
              )}
            </pattern>
          );
        })}
      </defs>
    );
  };

  // ---------------------------------------------------------------------------
  const getConditionFill = (condition: any) => {
    const pattern = condition.pattern || 'solid';
    if (pattern === 'solid') {
      return condition.color;
    }

    return `url(#pattern-${condition.id}-${id})`;
  };

  // ---------------------------------------------------------------------------
  const renderConditionBackground = () => {
    if (!personData.conditions || personData.conditions.length === 0) {
      return null;
    }

    const conditions = personData.conditions;
    const numConditions = conditions.length;

    // For standard shapes (male/female), we'll use CSS gradients for solid colors and SVG for patterns
    if (personData.gender === 'M') {
      // Square shape
      if (numConditions === 1) {
        const condition = conditions[0];
        const pattern = condition.pattern || 'solid';

        if (pattern === 'solid') {
          return (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: condition.color,
                zIndex: 1,
              }}
            />
          );
        } else {
          // Use SVG for patterns
          return (
            <svg
              width={NODE_SIZE - BORDER_WIDTH * 2}
              height={NODE_SIZE - BORDER_WIDTH * 2}
              style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
            >
              {renderPatternDefs([condition])}
              <rect
                width={NODE_SIZE - BORDER_WIDTH * 2}
                height={NODE_SIZE - BORDER_WIDTH * 2}
                fill={getConditionFill(condition)}
              />
            </svg>
          );
        }
      } else if (numConditions === 2) {
        const hasPatterns = conditions.some(
          (c) => c.pattern && c.pattern !== 'solid'
        );

        if (!hasPatterns) {
          return (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, ${conditions[0].color} 50%, ${conditions[1].color} 50%)`,
                zIndex: 1,
              }}
            />
          );
        } else {
          // Use SVG for patterns
          return (
            <svg
              width={NODE_SIZE - BORDER_WIDTH * 2}
              height={NODE_SIZE - BORDER_WIDTH * 2}
              style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
            >
              {renderPatternDefs(conditions)}
              <rect
                width={NODE_SIZE / 2}
                height={NODE_SIZE - BORDER_WIDTH * 2}
                fill={getConditionFill(conditions[0])}
              />
              <rect
                x={NODE_SIZE / 2}
                width={NODE_SIZE / 2}
                height={NODE_SIZE - BORDER_WIDTH * 2}
                fill={getConditionFill(conditions[1])}
              />
            </svg>
          );
        }
      } else {
        // For multiple conditions, create radial sections using SVG
        const angleStep = 360 / numConditions;
        return (
          <svg
            width={NODE_SIZE - BORDER_WIDTH * 2}
            height={NODE_SIZE - BORDER_WIDTH * 2}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
          >
            {renderPatternDefs(conditions)}
            {conditions.map((condition: any, index: number) => {
              const startAngle = (index * angleStep - 90) * (Math.PI / 180);
              const endAngle = ((index + 1) * angleStep - 90) * (Math.PI / 180);
              const centerX = (NODE_SIZE - BORDER_WIDTH * 2) / 2;
              const centerY = (NODE_SIZE - BORDER_WIDTH * 2) / 2;
              const radius = 60;

              const x1 = centerX + radius * Math.cos(startAngle);
              const y1 = centerY + radius * Math.sin(startAngle);
              const x2 = centerX + radius * Math.cos(endAngle);
              const y2 = centerY + radius * Math.sin(endAngle);

              const largeArcFlag = angleStep > 180 ? 1 : 0;

              return (
                <path
                  key={condition.id}
                  d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                  fill={getConditionFill(condition)}
                />
              );
            })}
          </svg>
        );
      }
    } else if (personData.gender === 'F') {
      // Circle shape - similar logic but with border-radius for CSS or clipping for SVG
      if (numConditions === 1) {
        const condition = conditions[0];
        const pattern = condition.pattern || 'solid';

        if (pattern === 'solid') {
          return (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: condition.color,
                borderRadius: '50%',
                zIndex: 1,
              }}
            />
          );
        } else {
          // Use SVG for patterns
          return (
            <svg
              width={NODE_SIZE}
              height={NODE_SIZE}
              style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
            >
              {renderPatternDefs([condition])}
              <circle
                cx={NODE_SIZE / 2 - BORDER_WIDTH}
                cy={NODE_SIZE / 2 - BORDER_WIDTH}
                r={(NODE_SIZE - 2 * BORDER_WIDTH) / 2}
                fill={getConditionFill(condition)}
              />
            </svg>
          );
        }
      } else if (numConditions === 2) {
        const hasPatterns = conditions.some(
          (c) => c.pattern && c.pattern !== 'solid'
        );

        if (!hasPatterns) {
          return (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, ${conditions[0].color} 50%, ${conditions[1].color} 50%)`,
                borderRadius: '50%',
                zIndex: 1,
              }}
            />
          );
        } else {
          // Use SVG for patterns with circular clipping
          return (
            <svg
              width={NODE_SIZE}
              height={NODE_SIZE}
              style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
            >
              <defs>
                <clipPath id={`circle-clip-${id}`}>
                  <circle
                    cx={NODE_SIZE / 2 - BORDER_WIDTH}
                    cy={NODE_SIZE / 2 - BORDER_WIDTH}
                    r={(NODE_SIZE - 2 * BORDER_WIDTH) / 2}
                  />
                </clipPath>
                {renderPatternDefs(conditions)}
              </defs>
              <rect
                width={NODE_SIZE / 2}
                height={NODE_SIZE}
                fill={getConditionFill(conditions[0])}
                clipPath={`url(#circle-clip-${id})`}
              />
              <rect
                x={NODE_SIZE / 2}
                width={NODE_SIZE / 2}
                height={NODE_SIZE}
                fill={getConditionFill(conditions[1])}
                clipPath={`url(#circle-clip-${id})`}
              />
            </svg>
          );
        }
      } else {
        // For multiple conditions, create radial sections using SVG
        const angleStep = 360 / numConditions;
        return (
          <svg
            width={NODE_SIZE}
            height={NODE_SIZE}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
          >
            <defs>
              <clipPath id={`circle-clip-${id}`}>
                <circle
                  cx={NODE_SIZE / 2 - BORDER_WIDTH}
                  cy={NODE_SIZE / 2 - BORDER_WIDTH}
                  r={(NODE_SIZE - 2 * BORDER_WIDTH) / 2}
                />
              </clipPath>
              {renderPatternDefs(conditions)}
            </defs>
            {conditions.map((condition: any, index: number) => {
              const startAngle = (index * angleStep - 90) * (Math.PI / 180);
              const endAngle = ((index + 1) * angleStep - 90) * (Math.PI / 180);
              const centerX = (NODE_SIZE - BORDER_WIDTH * 2) / 2;
              const centerY = (NODE_SIZE - BORDER_WIDTH * 2) / 2;
              const radius = 60;

              const x1 = centerX + radius * Math.cos(startAngle);
              const y1 = centerY + radius * Math.sin(startAngle);
              const x2 = centerX + radius * Math.cos(endAngle);
              const y2 = centerY + radius * Math.sin(endAngle);

              const largeArcFlag = angleStep > 180 ? 1 : 0;

              return (
                <path
                  key={condition.id}
                  d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                  fill={getConditionFill(condition)}
                  clipPath={`url(#circle-clip-${id})`}
                />
              );
            })}
          </svg>
        );
      }
    }

    return null;
  };
  // ---------------------------------------------------------------------------
  const renderConditionMarkersForSpecialShapes = () => {
    if (!personData.conditions || personData.conditions.length === 0) {
      return null;
    }

    if (personData.gender !== 'U' && personData.gender !== 'O') {
      return null;
    }

    const conditions = personData.conditions;
    const numConditions = conditions.length;

    if (personData.gender === 'U') {
      // Diamond shape using SVG
      if (numConditions === 1) {
        return (
          <svg
            width={NODE_SIZE}
            height={NODE_SIZE}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
          >
            {renderPatternDefs([conditions[0]])}
            <polygon
              points={`${NODE_SIZE / 2},0 ${NODE_SIZE},${NODE_SIZE / 2} ${
                NODE_SIZE / 2
              },${NODE_SIZE} 0,${NODE_SIZE / 2}`}
              fill={getConditionFill(conditions[0])}
              stroke="transparent"
            />
          </svg>
        );
      } else {
        // For multiple conditions in diamond, create sections
        const angleStep = 360 / numConditions;
        return (
          <svg
            width={NODE_SIZE}
            height={NODE_SIZE}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
          >
            <defs>
              <clipPath id={`diamond-clip-${id}`}>
                <polygon
                  points={`${NODE_SIZE / 2},0 ${NODE_SIZE},${NODE_SIZE / 2} ${
                    NODE_SIZE / 2
                  },${NODE_SIZE} 0,${NODE_SIZE / 2}`}
                />
              </clipPath>
              {renderPatternDefs(conditions)}
            </defs>
            {conditions.map((condition: any, index: number) => {
              const startAngle = (index * angleStep - 90) * (Math.PI / 180);
              const endAngle = ((index + 1) * angleStep - 90) * (Math.PI / 180);
              const centerX = NODE_SIZE / 2;
              const centerY = NODE_SIZE / 2;
              const radius = NODE_SIZE;

              const x1 = centerX + radius * Math.cos(startAngle);
              const y1 = centerY + radius * Math.sin(startAngle);
              const x2 = centerX + radius * Math.cos(endAngle);
              const y2 = centerY + radius * Math.sin(endAngle);

              const largeArcFlag = angleStep > 180 ? 1 : 0;

              return (
                <path
                  key={condition.id}
                  d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                  fill={getConditionFill(condition)}
                  clipPath={`url(#diamond-clip-${id})`}
                />
              );
            })}
          </svg>
        );
      }
    } else if (personData.gender === 'O') {
      // Triangle shape using SVG
      if (numConditions === 1) {
        return (
          <svg
            width={NODE_SIZE}
            height={NODE_SIZE}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
          >
            {renderPatternDefs([conditions[0]])}
            <polygon
              points="40,0 80,72 0,72"
              fill={getConditionFill(conditions[0])}
              stroke="transparent"
            />
          </svg>
        );
      } else {
        // For multiple conditions in triangle, create sections
        const angleStep = 360 / numConditions;
        return (
          <svg
            width="80"
            height="80"
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
          >
            <defs>
              <clipPath id={`triangle-clip-${id}`}>
                <polygon points="40,0 80,72 0,72" />
              </clipPath>
              {renderPatternDefs(conditions)}
            </defs>
            {conditions.map((condition: any, index: number) => {
              const startAngle = (index * angleStep - 90) * (Math.PI / 180);
              const endAngle = ((index + 1) * angleStep - 90) * (Math.PI / 180);
              const centerX = 40;
              const centerY = 48; // Adjusted for triangle center
              const radius = 80;

              const x1 = centerX + radius * Math.cos(startAngle);
              const y1 = centerY + radius * Math.sin(startAngle);
              const x2 = centerX + radius * Math.cos(endAngle);
              const y2 = centerY + radius * Math.sin(endAngle);

              const largeArcFlag = angleStep > 180 ? 1 : 0;

              return (
                <path
                  key={condition.id}
                  d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                  fill={getConditionFill(condition)}
                  clipPath={`url(#triangle-clip-${id})`}
                />
              );
            })}
          </svg>
        );
      }
    }

    return null;
  };

  // ---------------------------------------------------------------------------
  const renderAdoptionIndicator = () => {
    if (!personData.isAdopted) {
      return null;
    }

    return (
      <Text
        size="9px"
        fw={700}
        c="dimmed"
        style={{
          position: 'absolute',
          top: '-4px',
          left: '-4px',
          fontSize: '9px',
        }}
      >
        A
      </Text>
    );
  };

  // ---------------------------------------------------------------------------
  const renderProbandArrow = () => {
    if (!personData.isProband) {
      return null;
    }

    return (
      <svg
        width="120"
        height="80"
        style={{
          position: 'absolute',
          bottom: '-60px',
          left: '0px',
          transform: 'translateX(-50%)',
          zIndex: 20,
        }}
      >
        {/* Arrow with tail pointing at node from 45-degree angle */}
        <line
          x1="20"
          y1="60"
          x2="50"
          y2="30"
          stroke="black"
          strokeWidth="2"
          markerEnd="url(#arrowhead-proband)"
        />
        {/* Arrow head definition */}
        <defs>
          <marker
            id="arrowhead-proband"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill="black" />
          </marker>
        </defs>
        {/* Proband label */}{' '}
        <text
          x="10"
          y="75"
          textAnchor="start"
          fontSize="11"
          fill="black"
          fontWeight="bold"
        >
          {t('personNode.probandLabel')}
        </text>
      </svg>
    );
  };
  // ---------------------------------------------------------------------------
  const renderDeceasedLine = () => {
    if (!personData.isDeceased) {
      return null;
    }

    if (personData.gender === 'U' || personData.gender === 'O') {
      // For special shapes, render the line as an SVG element
      return (
        <svg
          width={NODE_SIZE}
          height={NODE_SIZE}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            zIndex: 3,
          }}
        >
          <line
            x1="0"
            y1={NODE_SIZE}
            x2={NODE_SIZE}
            y2="0"
            stroke="var(--mantine-color-dark-9)"
            strokeWidth="2"
          />
        </svg>
      );
    }

    return (
      <div
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          pointerEvents: 'none',
          zIndex: 3,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: '100%',
            width: '1px',
            height: '141%', // sqrt(2) * 100% for diagonal
            backgroundColor: 'var(--mantine-color-dark-9)',
            transformOrigin: 'top left',
            transform: 'rotate(45deg)',
          }}
        />
      </div>
    );
  };
  // ---------------------------------------------------------------------------
  const renderSpecialShape = () => {
    const strokeColor = '#333';
    const strokeWidth = personData.isProband ? 3 : 2;
    // Make fill transparent so condition backgrounds show through
    const fillColor =
      personData.conditions && personData.conditions.length > 0
        ? 'transparent'
        : '#ffffff';

    if (personData.gender === 'U') {
      // Diamond shape - sized to match the visual footprint of square/circle (full 80x80 area + 4px)
      return (
        <svg
          width={NODE_SIZE}
          height={NODE_SIZE}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 2 }}
        >
          <polygon
            points={`${NODE_SIZE / 2},0 ${NODE_SIZE},${NODE_SIZE / 2} ${
              NODE_SIZE / 2
            },${NODE_SIZE} 0,${NODE_SIZE / 2}`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );
    } else if (personData.gender === 'O') {
      // Triangle shape - sized to match the visual footprint of square/circle (full 80x80 area + 4px)
      return (
        <svg
          width="80"
          height="80"
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 2 }}
        >
          <polygon
            points="40,0 80,72 0,72"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );
    }

    return null;
  };

  // ---------------------------------------------------------------------------
  const renderDocumentedEvaluationMarker = () => {
    if (!personData.geneticData?.hasDocumentedEvaluation) {
      return null;
    }

    return <div className={classes.documentedEvaluationMarker}>*</div>;
  };

  // ---------------------------------------------------------------------------
  const renderCarrierMarker = () => {
    if (!personData.geneticData?.isCarrier) {
      return null;
    }

    const centerX = NODE_SIZE / 2 - 1;
    const centerY = NODE_SIZE / 2 - 1;
    const radius = 6;

    return (
      <svg
        className={classes.carrierMarker}
        width={NODE_SIZE}
        height={NODE_SIZE}
      >
        <circle cx={centerX} cy={centerY} r={radius} fill="#000000" />
      </svg>
    );
  };

  // ---------------------------------------------------------------------------
  const renderAsymptomaticCarrierMarker = () => {
    if (!personData.geneticData?.isAsymptomaticCarrier) {
      return null;
    }

    const centerX = NODE_SIZE / 2 - 1;

    return (
      <svg
        className={classes.asymptomaticCarrierMarker}
        width={NODE_SIZE}
        height={NODE_SIZE}
      >
        <line
          x1={centerX}
          y1="0"
          x2={centerX}
          y2={NODE_SIZE - 1}
          stroke="#000000"
          strokeWidth="2"
        />
      </svg>
    );
  };

  // ---------------------------------------------------------------------------
  const renderHeredityIndicator = () => {
    if (!displayingHeredity) {
      return null;
    }

    let customPath = '';
    const viewBox = `0 0 20 20`;

    if (personData.heredity === 'childless') {
      customPath = `M10,0 L10,10 M0,10 L20,10`;
    } else if (personData.heredity === 'infertile') {
      customPath = `M10,0 L10,10 M0,10 L20,10 M0,14 L20,14`;
    }

    return (
      <svg
        width={`20px`}
        height={`20px`}
        viewBox={viewBox}
        className={classes.heredityMarker}
      >
        <path
          d={customPath}
          stroke="#333"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    );
  };
  // ---------------------------------------------------------------------------
  const renderPregnancyMarker = () => {
    if (!personData.isPregnancy) {
      return null;
    }

    return (
      <Box w={NODE_SIZE} h={NODE_SIZE} className={classes.nodeInnerMarker}>
        <Text
          size="xl"
          fw={700}
          c="dark"
          style={{
            textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
          }}
        >
          P
        </Text>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  const renderMultiplePeopleMarker = () => {
    if (!personData.isMultiplePeople || !personData.multiplePeopleCount) {
      return null;
    }

    return (
      <Box w={NODE_SIZE} h={NODE_SIZE} className={classes.nodeInnerMarker}>
        <Text
          size="xl"
          fw={700}
          c="dark"
          style={{
            textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
          }}
        >
          {personData.multiplePeopleCount}
        </Text>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  return (
    <Box style={{ position: 'relative' }}>
      {/* The actual node shape */}
      <Box style={getPersonStyle()} onContextMenu={handleContextMenu}>
        {/* Condition backgrounds for standard shapes */}
        {(personData.gender === 'M' || personData.gender === 'F') &&
          renderConditionBackground()}
        {/* Special shapes for unknown and other genders with condition backgrounds */}
        {(personData.gender === 'U' || personData.gender === 'O') &&
          renderConditionMarkersForSpecialShapes()}
        {/* Special shapes borders (rendered on top of condition backgrounds) */}
        {(personData.gender === 'U' || personData.gender === 'O') &&
          renderSpecialShape()}
        {/* Handles for connections */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          isConnectable={isConnectable}
          className={classes.baseHandle}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          isConnectable={isConnectable}
          className={classes.baseHandle}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left-source"
          isConnectable={isConnectable}
          className={cx(classes.baseHandle, classes.leftHandle)}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left-target"
          isConnectable={isConnectable}
          className={classes.baseHandle}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right-source"
          isConnectable={isConnectable}
          className={cx(classes.baseHandle, classes.rightHandle)}
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right-target"
          isConnectable={isConnectable}
          className={classes.baseHandle}
        />{' '}
        {/* Special indicators */}
        {renderAdoptionIndicator()}
        {renderHeredityIndicator()}
        {renderDeceasedLine()}
        {renderProbandArrow()}
        {renderPregnancyMarker()}
        {renderMultiplePeopleMarker()}
        {/* Genetic markers */}
        {renderDocumentedEvaluationMarker()}
        {renderCarrierMarker()}
        {renderAsymptomaticCarrierMarker()}
      </Box>
      {/* Text content displayed below the node */}
      <Box
        style={{
          position: 'absolute',
          top: `${displayingHeredity ? NODE_SIZE + 15 : NODE_SIZE + 5}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          width: '120px',
          zIndex: 5,
        }}
      >
        {/* Person label */}
        <Text size="11px" fw={700} ta="center" lineClamp={3}>
          {personData.label ||
            (personData.fName || personData.lName
              ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
                `${personData.fName || ''} ${personData.lName || ''}`
              : '')}
        </Text>
        {/* Evaluation markers */}
        {personData.geneticData?.evaluations &&
          personData.geneticData.evaluations.length > 0 && (
            <Text size="9px" fw={700} ta="center" c="dark">
              {personData.geneticData.evaluations.map((evaluation, index) => {
                const evaluations = personData.geneticData?.evaluations || [];
                let label = evaluation.label;
                if (evaluation.isPositive) {
                  label += '+';
                }
                if (evaluation.isUninformative) {
                  label += 'u';
                }
                return (
                  <span key={evaluation.id}>
                    {label}
                    {evaluation.testResults && ` (${evaluation.testResults})`}
                    {index < evaluations.length - 1 && ', '}
                  </span>
                );
              })}
            </Text>
          )}
        {/* Age */}{' '}
        {calculatedAge && (
          <Text size="9px" ta="center">
            {personData.isDeceased && personData.ageAtDeath
              ? t('personNode.ageAtDeathLabel')
              : t('personNode.ageLabel')}
            : {calculatedAge}
          </Text>
        )}
        {personData.notes && (
          <Text size="9px" ta="center" c="dimmed" lineClamp={3}>
            {personData.notes}
          </Text>
        )}
      </Box>
    </Box>
  );
};
